import { DecimalPipe, I18nPluralPipe, JsonPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, output, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, ControlValueAccessor, FormGroup, ValidationErrors, Validator } from '@angular/forms';
import { MatError, MatHint } from '@angular/material/form-field';

import { IntegerFormControl, NumberFormRangeStrategy } from 'forms-data';
import { provideValueAccessor } from 'forms-ui';
import { CartStepperInputComponent } from '../../../../../../common/quantity-selector';
import { CustomLength } from '../../../types';
import { ProductUnitLabels, QuantityItemPriceModel } from 'products-data';
import { CurrencyHtmlPipe } from 'common-util';
// import { CurrencyHtmlPipe } from 'common-util';

@Component({
  selector: 'ideal-wire-cut-custom-length-view',
  imports: [CartStepperInputComponent, MatError, MatHint, DecimalPipe, I18nPluralPipe, CurrencyHtmlPipe, JsonPipe],
  templateUrl: './wire-cut-custom-length-view.component.html',
  styleUrl: './wire-cut-custom-length-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideValueAccessor(WireCutCustomLengthViewComponent)],
  host: {
    // '(focusout)': 'onFocusOut($event)',
  },
})
export class WireCutCustomLengthViewComponent implements ControlValueAccessor, Validator {
  public readonly conflicted = input<boolean>(false);
  public readonly pricing = input<QuantityItemPriceModel>();
  public readonly mode = input<'add' | 'edit'>('add');

  readonly lengthEnter = output<void>();
  readonly quantityEnter = output<void>();
  readonly deleteRequested = output<void>();

  private readonly _lengthStepper = viewChild<CartStepperInputComponent>('lengthStepper');
  private readonly _quantityStepper = viewChild<CartStepperInputComponent>('quantityStepper');

  private readonly _elementRef = inject(ElementRef);
  private _onChange: (value: CustomLength | null) => void = () => {};
  private _onTouched: () => void = () => {};

  protected readonly soldInMultiplesOf = computed(() => this.pricing()?.pricingModel.quantitySoldIn ?? 1);
  protected readonly quantityClearMode = computed(() => this.mode() === 'add');
  protected readonly displayUnitLabel = computed(() => this.pricing()?.pricingModel.unitLabelLocalized ?? ProductUnitLabels.ea);

  readonly lengthControl = new IntegerFormControl(null, {
    min: 1,
    step: 1,
    required: true,
    rangeStrategy: NumberFormRangeStrategy.Restrict,
  });

  readonly quantityControl = new IntegerFormControl(null, {
    min: 1,
    step: 1,
    required: true,
    rangeStrategy: NumberFormRangeStrategy.Restrict,
  });

  protected readonly formGroup = new FormGroup({
    length: this.lengthControl,
    quantity: this.quantityControl,
  });

  private _isWritingValue = false;

  constructor() {
    this.formGroup.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      if (!this._isWritingValue) {
        this._onChange(this._currentValue());
      }
    });
  }

  private _currentValue(): CustomLength | null {
    const { length, quantity } = this.formGroup.value;
    if (length == null || quantity == null) {
      return null;
    }
    return { length, quantity };
  }

  writeValue(v: CustomLength | null): void {
    this._isWritingValue = true;
    if (v != null) {
      this.lengthControl.setValue(v.length);
      this.quantityControl.setValue(v.quantity);
    } else {
      this.lengthControl.setValue(null);
      this.quantityControl.setValue(null);
    }
    this._isWritingValue = false;
  }

  public reset(): void {
    this.formGroup.reset();
  }

  protected get totalLen(): number {
    return (this.lengthControl.value ?? 0) * (this.quantityControl.value ?? 0);
  }

  registerOnChange(fn: (value: CustomLength | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  validate(_: AbstractControl): ValidationErrors | null {
    return this.formGroup.valid ? null : { invalid: true };
  }

  public focusLength(): void {
    this._lengthStepper()?.focus();
  }

  public focusQuantity(): void {
    this._quantityStepper()?.focus();
  }

  protected onLengthEnter(): void {
    this.lengthEnter.emit();
    this.focusQuantity();
  }

  protected onQuantityEnter(): void {
    this.quantityEnter.emit();
  }

  protected onQuantityDecrement(): void {
    // In edit mode, if quantity is at minimum, request deletion
    if (this.mode() === 'edit' && this.quantityControl.value !== null && this.quantityControl.value <= 1) {
      this.deleteRequested.emit();
    }
  }

  public focus(): void {
    this._lengthStepper()?.focus();
  }
}
